// Lane filtering for the Mission Control tables — region/sector narrowing over
// board-supplied rows. Pure: no React, no repo imports, no I/O, so every case
// (including hostile rows) is reachable from a test without a network.
//
// The rows arrive from the network as plain objects keyed by column label plus
// hidden ids. `region` and `sector` are hidden keys the board MAY carry; today
// no row carries either, so every entry point must be correct on absence.
//
// Two rules run through the whole file:
//   1. Own-property only. A board string is never used to index a bare object
//      literal, and a value is only a lane value if the row OWNS it. An
//      inherited `region` is not this row's region.
//   2. No vocabulary is declared here. There is no list of regions and no list
//      of sectors anywhere in this file — options are derived from the rows.

/** Sentinel for "no narrowing on this axis". The leading space keeps it out of
 *  the board's value space: a real board value is trimmed before it is stored,
 *  so it can never equal this. */
export const LANE_ALL = ' ALL';

export type Lane = { region?: string; sector?: string };

/**
 * The row's OWN value for `key`, as a trimmed non-empty string, or undefined.
 * Never throws, and never walks the prototype chain.
 */
function ownLaneValue(row: unknown, key: string): string | undefined {
  if (row === null || typeof row !== 'object') return undefined;
  if (!Object.prototype.hasOwnProperty.call(row, key)) return undefined;
  const value = (row as Record<string, unknown>)[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The lane a row declares. A half is present only when the row owns that key as
 * a non-empty string; missing / null / number / object / empty / whitespace all
 * yield undefined for that half.
 */
export function readLane(row: unknown): Lane {
  const lane: Lane = {};
  const region = ownLaneValue(row, 'region');
  if (region !== undefined) lane.region = region;
  const sector = ownLaneValue(row, 'sector');
  if (sector !== undefined) lane.sector = sector;
  return lane;
}

/**
 * Can this data be filtered at all? True iff at least one row carries an own
 * region or an own sector. False for today's rows, which carry neither.
 */
export function laneFilterable(rows: readonly unknown[]): boolean {
  if (!Array.isArray(rows)) return false;
  for (const row of rows) {
    if (ownLaneValue(row, 'region') !== undefined) return true;
    if (ownLaneValue(row, 'sector') !== undefined) return true;
  }
  return false;
}

/** Distinct present values of `key`, sorted by localeCompare. A Set — never a
 *  bare object literal indexed by a board string. */
function distinct(rows: readonly unknown[], key: string, keep: (row: unknown) => boolean): string[] {
  const seen = new Set<string>();
  if (!Array.isArray(rows)) return [];
  for (const row of rows) {
    if (!keep(row)) continue;
    const value = ownLaneValue(row, key);
    if (value !== undefined) seen.add(value);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

/** True if the row's own value on this axis satisfies the selection. LANE_ALL
 *  matches everything, including rows that lack the key. A specific selection
 *  matches only an exact (trimmed) string equality — never substring, never
 *  case-insensitive — so a row that lacks the key cannot match it. */
function axisMatches(row: unknown, key: string, selection: string): boolean {
  if (selection === LANE_ALL) return true;
  const value = ownLaneValue(row, key);
  return value !== undefined && value === selection;
}

/** The distinct regions present across rows. */
export function regionOptions(rows: readonly unknown[]): string[] {
  return distinct(rows, 'region', () => true);
}

/** The distinct sectors present on rows in `region` — so a region with no
 *  mechanics does not offer Mechanics. LANE_ALL widens to every row. */
export function sectorOptions(rows: readonly unknown[], region: string): string[] {
  return distinct(rows, 'sector', (row) => axisMatches(row, 'region', region));
}

/** Rows matching BOTH axes. Default (LANE_ALL/LANE_ALL) is everything, in the
 *  original order; a selection is only ever a narrowing. */
export function applyLane(rows: readonly unknown[], region: string, sector: string): unknown[] {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => axisMatches(row, 'region', region) && axisMatches(row, 'sector', sector));
}
